/**
 * V8.3.2 — Registro remoto de eventos e erros (Web e Stick).
 *
 * Junta eventos importantes (erros de reprodução, motivo real da falha, eventos do player nativo do Stick,
 * erros de JavaScript) e envia em LOTE para o servidor (api/telemetry.php?action=report_batch).
 * Aparecem no Gerenciador (Auditoria → Telemetria de Erros) e em api/claude.php (leitura para diagnóstico).
 *
 * Leve: no máximo 1 envio a cada 20 s (erros: em 3 s), até 60 eventos por envio, fila limitada a 200.
 * Nunca atrapalha o app: qualquer falha de envio é ignorada (o evento volta para a fila uma vez).
 */
import { APP_VERSION } from '../version';

export type RemoteSeverity = 'info' | 'warning' | 'error' | 'critical';

interface RemoteEvent {
  type: string;
  severity: RemoteSeverity;
  message: string;
  content_type?: string;
  content_name?: string;
  at: string;
}

const queue: RemoteEvent[] = [];
const MAX_QUEUE = 200;
let timer: ReturnType<typeof setTimeout> | null = null;
let sending = false;
let installed = false;

function nowStr(): string {
  try {
    return new Date().toLocaleTimeString('pt-BR', { hour12: false, timeZone: 'America/Sao_Paulo' });
  } catch {
    return new Date().toISOString();
  }
}

function deviceId(): string {
  try {
    return localStorage.getItem('iptv_device_id') || '';
  } catch {
    return '';
  }
}

function schedule(ms: number) {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flushRemoteLog();
  }, ms);
}

/** Registra um evento. Erros vão em ~3 s; o resto junta e vai em ~20 s. */
export function remoteLog(
  type: string,
  message: string,
  severity: RemoteSeverity = 'info',
  extra: { content_type?: string; content_name?: string } = {}
): void {
  try {
    if (!message) return;
    queue.push({ type, severity, message: String(message).slice(0, 2000), at: nowStr(), ...extra });
    while (queue.length > MAX_QUEUE) queue.shift();
    if (severity === 'error' || severity === 'critical') {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      schedule(3000);
    } else {
      schedule(20000);
    }
  } catch {}
}

export async function flushRemoteLog(): Promise<void> {
  if (sending || queue.length === 0) return;
  sending = true;
  const batch = queue.splice(0, 60);
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const token = localStorage.getItem('nexus_auth_token');
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {}
    let profileId = '';
    try {
      profileId = localStorage.getItem('nexus_iptv_active_profile_id') || '';
    } catch {}
    const res = await fetch('/api/telemetry.php?action=report_batch', {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify({
        events: batch,
        device_id: deviceId(),
        profile_id: profileId,
        app_version: APP_VERSION,
        device_info: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 250),
      }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch {
    // devolve à fila (uma vez) para tentar no próximo envio
    if (queue.length + batch.length <= MAX_QUEUE) queue.unshift(...batch);
  } finally {
    sending = false;
    if (queue.length > 0) schedule(20000);
  }
}

/** Instala a captura de erros de JavaScript não tratados (uma vez, na abertura do app). */
export function installRemoteErrorCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e: ErrorEvent) => {
    const where = e.filename ? ` (${String(e.filename).split('/').pop()}:${e.lineno})` : '';
    remoteLog('js_error', `${e.message || 'erro de JavaScript'}${where}`, 'error');
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const r: any = e.reason;
    remoteLog('js_error', `promessa rejeitada: ${r?.message || String(r).slice(0, 300)}`, 'warning');
  });
  window.addEventListener('pagehide', () => {
    flushRemoteLog();
  });
}
